import type { KeyboardEvent, MouseEvent } from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { Icon } from "../../../components/Icon/Icon";

/**
 * Vertical rhythm of the nav list — one source of truth.
 *
 * The rail and the expanded panel are the SAME list in two widths, so the row
 * height and the gap between rows must not be re-declared per state.
 * `Sidebar.tsx` imports these for the list container's `rowGap`; the item uses
 * them for its own box. Change them here and both states follow.
 */
export const ITEM_HEIGHT = 44;
/* 4px — the rail's original gap (the old `spacing={0.5}`). The rail was the
   state that was already correct, so the expanded panel adopts its rhythm and
   not the other way round. */
export const ITEM_GAP = 4;

/** Wording lives in the Sidebar, where `t()` is; MenuItem owns the styling. */
export type MenuItemBadge = { label: string; tone: "pro" | "soon" };

type MenuItemProps = {
  iconName: string;
  text: string;
  /** Destination. Present => the row renders as a real <a href>. */
  to?: string;
  /** Icon glyph size. */
  width?: number | string;
  height?: number | string;
  expanded: boolean;
  active?: boolean;
  /** Subscription-gated: still clickable, but opens the upgrade modal. */
  locked?: boolean;
  comingSoon?: boolean;
  /** Full tooltip title, already translated. Falsy => no tooltip. */
  tooltip?: string;
  badge?: MenuItemBadge;
  onClick?: (e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => void;
};

const BADGE_TONE = {
  pro: { color: "#F59E0B", backgroundColor: "rgba(245,158,11,0.15)" },
  soon: { color: "#60A5FA", backgroundColor: "rgba(96,165,250,0.14)" },
} as const;

export const MenuItem: React.FC<MenuItemProps> = ({
  iconName,
  expanded,
  text,
  to,
  width = 24,
  height = 24,
  active = false,
  locked = false,
  comingSoon = false,
  tooltip,
  badge,
  onClick,
}) => {
  const reduceMotion = useReducedMotion();

  const navigable = !comingSoon && !!onClick;

  /*
   * Real anchors for real destinations.
   *
   * These rows were <div onClick>, so ctrl/cmd-click, middle-click, "open in
   * new tab" and the browser's status-bar URL preview all did nothing — the
   * single most common complaint about hand-rolled SPA menus. An <a href> gets
   * all of that for free, plus Enter-to-activate and correct screen-reader
   * semantics, with none of the ARIA scaffolding a div needs.
   *
   * Locked entries deliberately get NO href: ctrl-clicking a feature the
   * subscription does not include must not open a page the API will refuse.
   * They stay buttons that open the upgrade modal.
   */
  const asLink = navigable && !!to && !locked;

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    if (!onClick) return;
    /* Let the browser handle new-tab intents rather than swallowing them. */
    if (asLink && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1)) return;
    e.preventDefault();
    onClick(e);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (!navigable) return;
    /* An <a href> already fires a click on Enter — synthesising a second one
       would navigate twice. Only the button-flavoured rows need this. */
    if (asLink) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault(); // Space would otherwise scroll the list
      onClick?.(e);
    }
  };

  /* Spread as a loose object: MUI's `component` override plus `href` fights
     Stack's generic typing otherwise. */
  const rootProps: Record<string, any> = asLink
    ? { component: "a", href: to }
    : { component: "div", role: navigable ? "button" : undefined };

  const row = (
    <Stack
      {...rootProps}
      direction="row"
      alignItems="center"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      /*
       * `aria-label` is unconditional on purpose. Collapsed, the visible label
       * is unmounted and the icon is a decorative SVG, so without this every
       * rail item announced as an unnamed link. Expanded it simply matches the
       * visible text.
       */
      aria-label={text}
      aria-current={active ? "page" : undefined}
      aria-disabled={comingSoon || undefined}
      tabIndex={navigable ? 0 : -1}
      sx={{
        position: "relative",
        /*
         * THE UNEVEN-SPACING BUG.
         *
         * The list is a flex column that overflows on any laptop screen, and
         * flex items shrink by default. With only `height` set, this row's
         * min-content height is the 24px icon, so the browser was free to
         * squeeze it well under 44px to fit — but ONLY in the expanded state.
         * Collapsed, the row sat inside the tooltip's wrapper, whose own
         * min-content height is this row's fixed 44px, so it could not shrink.
         * Two states, two rhythms, from the same markup.
         *
         * `minHeight` + `flexShrink: 0` make the height a promise instead of a
         * suggestion: 44px in every state, and the list scrolls (it has both a
         * scrollbar and fade affordances) rather than squeezing.
         */
        height: ITEM_HEIGHT,
        minHeight: ITEM_HEIGHT,
        flexShrink: 0,
        mx: 1,
        px: expanded ? 1.5 : 0,
        borderRadius: 2.5,
        cursor: comingSoon ? "default" : "pointer",
        justifyContent: expanded ? "flex-start" : "center",
        gap: 1.5,
        /* The row is an <a> now: kill the UA underline and blue. */
        textDecoration: "none",
        WebkitTapHighlightColor: "transparent",
        color: active && !comingSoon ? "#FFFFFF" : "var(--c-nav-muted)",
        backgroundColor: active ? "rgba(37,99,235,0.20)" : "transparent",
        opacity: comingSoon ? 0.55 : 1,
        transition: "background-color 160ms ease, color 160ms ease",
        // The default focus ring is invisible against this panel.
        "&:focus-visible": {
          outline: "2px solid #60A5FA",
          outlineOffset: -2,
        },
        "&:hover": comingSoon
          ? {}
          : {
              backgroundColor: active ? "rgba(37,99,235,0.26)" : "rgba(96,165,250,0.12)",
              color: "var(--c-nav-text)",
            },
      }}
    >
      {/* Active accent strip. `layoutId` slides it between rows on navigation;
          with reduced motion it simply appears in place. */}
      {active && (
        <Box
          component={motion.div}
          layoutId="sidebar-active"
          transition={reduceMotion ? { duration: 0 } : undefined}
          sx={{
            position: "absolute",
            left: -8,
            top: 8,
            bottom: 8,
            width: 3,
            borderRadius: 999,
            backgroundColor: "#60A5FA",
          }}
        />
      )}

      <Box
        aria-hidden
        sx={{
          position: "relative",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          width: 24,
          height: 24,
        }}
      >
        <Icon name={iconName} width={width} height={height} />
        {/*
         * Collapsed, the PRO / BIENTÔT badge is unmounted with the label, so
         * the rail gave no hint that an item was gated — the user only found
         * out by clicking and getting the upgrade modal. A 6px dot carries
         * that state in the width the rail has.
         */}
        {!expanded && badge && (
          <Box
            sx={{
              position: "absolute",
              top: -1,
              right: -1,
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: BADGE_TONE[badge.tone].color,
              boxShadow: "0 0 0 2px var(--c-nav)",
            }}
          />
        )}
      </Box>

      {expanded && (
        <Typography
          component={motion.span}
          initial={reduceMotion ? false : { opacity: 0, x: -6 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.18 }}
          sx={{
            fontSize: 13.5,
            fontWeight: active ? 700 : 500,
            whiteSpace: "nowrap",
            /* The label must never push the badge out of the panel; it
               truncates instead. */
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
            color: "inherit",
            flex: 1,
          }}
        >
          {text}
        </Typography>
      )}

      {expanded && badge && (
        <Box
          component={motion.span}
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          sx={{
            flexShrink: 0,
            fontSize: 10,
            fontWeight: 600,
            px: 0.8,
            py: 0.2,
            borderRadius: 1,
            whiteSpace: "nowrap",
            letterSpacing: 0.2,
            ...BADGE_TONE[badge.tone],
          }}
        >
          {badge.label}
        </Box>
      )}
    </Stack>
  );

  /*
   * The tooltip wraps the ROW, not a wrapper around it.
   *
   * It used to sit on an inert <Box>, and MUI attaches its focus listeners to
   * whatever child it is given — so the tooltip was mouse-only and a keyboard
   * user tabbing down the collapsed rail got no label at all. On the row it
   * responds to focus as well as hover.
   */
  const rowWithTooltip = tooltip ? (
    <Tooltip title={tooltip} placement="right" arrow>
      {row}
    </Tooltip>
  ) : (
    row
  );

  /*
   * ONE wrapper in every state, and it is an <li>.
   *
   * Previously the expanded state returned the row bare while the collapsed
   * state returned it inside the tooltip's <Box>, so the list's direct child
   * was a different element per state — and a flex container sizes its direct
   * children, which is why the two states could never agree on a rhythm. The
   * shell is now unconditional and carries the list semantics the nav was
   * missing (`<nav><ul><li><a>`), so assistive tech announces "list, 16 items"
   * instead of a run of anonymous links.
   */
  return (
    <Box
      component="li"
      sx={{ display: "flex", flexDirection: "column", flexShrink: 0, listStyle: "none" }}
    >
      {rowWithTooltip}
    </Box>
  );
};
